import { Auth, User as FirebaseUser, UserCredential } from 'firebase/auth';
import { useTranslation } from 'next-i18next';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { JWT } from '@bridebook/toolbox/src/jwt';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { AuthSessionData, LoginOrSignupResult } from '@bridebook/web/lib/api/authenticate/types';
import {
  handleChangePassword,
  handleCordovaOauth,
  handleDeleteAccount,
  handleInitialize,
  handleLogin,
  handleLogout,
  handleRedirectAfterAuth,
  handleRegister,
  handleSetupUserAfterLoginOrSignup,
} from '@bridebook/web/lib/auth/auth-context';
import { getCollaboratorInvite } from '@bridebook/web/lib/auth/selectors';
import { AuthProviders, ICredentialsFields } from '@bridebook/web/lib/auth/types';
import { getFirebaseApp } from '@bridebook/web/lib/configure-deps';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSelector, useStore } from '@bridebook/web/lib/utils';
import { assertExists } from '@bridebook/web/lib/utils/assertExists';
import type { IUserSerialized } from '@bridebook/web/lib/users/types';
import type { TWeddingSerialized } from '@bridebook/web/lib/weddings/types';
import { useRouter } from 'next/router';
import { fetchy } from '../../lib/fetchy';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';

export const LOGOUT_DELAY_TIME = 3000;

export type AuthStateType = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: IUserSerialized | null;
  wedding: TWeddingSerialized | null;
};

type TAuthResult = Promise<LoginOrSignupResult | undefined>;

export type TRedirectAfterLoginOrSignup = (authSuccessResult: AuthSessionData) => Promise<void>;

type FirebaseContextType = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: IUserSerialized | null;
  wedding: TWeddingSerialized | null;
  login: (authRequest: TAuthRequest) => TAuthResult;
  register: (authRequest: TAuthRequest) => TAuthResult;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  loginOrSignupWithGoogle: (params: Pick<TAuthRequest, 'countryCode' | 'locale'>) => TAuthResult;
  loginOrSignupWithFacebook: (params: Pick<TAuthRequest, 'countryCode' | 'locale'>) => TAuthResult;
  loginOrSignupWithApple: (params: Pick<TAuthRequest, 'countryCode' | 'locale'>) => TAuthResult;
  redirectAfterLoginOrSignup: TRedirectAfterLoginOrSignup;
  changePassword: (
    actionCode: string,
    authFields: ICredentialsFields,
    countryCode: CountryCodes,
    locale: string,
    continueUrl?: string,
  ) => Promise<void>;
  prepareRedirectAfterLoginOrSignup: (result: LoginOrSignupResult) => void;
  triggerRedirectAfterLoginOrSignup: () => Promise<void>;
};

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  wedding: null,
};

const AuthContext = createContext<FirebaseContextType | null>(null);
export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuthContext context must be use inside AuthProvider');

  return context;
};

const firebaseApp = getFirebaseApp();

type AuthProviderProps = {
  children: ReactNode;
};

export type TAuthRequest = {
  countryCode: CountryCodes;
  locale: string;
} & (
  | {
      type: 'password';
      email: string;
      password: string;
      nonceId?: string;
      nonceSecret?: string;
    }
  | { type: 'social'; provider: AuthProviders }
  | { type: 'magic'; token: string } //we could add continueUrl here to redirect the user to the page after login or signup
);

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthStateType>(initialState);
  const store = useStore();
  const router = useRouter();
  const { nonceId, nonceSecret } = useSelector(getCollaboratorInvite);
  const { t } = useTranslation('settings');
  const alreadyInitializedRef = useRef(false);
  const authResultRef = useRef<LoginOrSignupResult | null>(null);
  const market = useMarket();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const { magicToken, ...query } = router.query;
  const triggeredMagicLogin = useRef(false);

  const handleSetAuthState = (value: AuthStateType) => {
    setAuthState(value);
  };

  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  const authStateRef = useRef<AuthStateType>(initialState);
  const loginOrRegisterInProgress = useRef(false);

  /**
   * Redirects user either to the home page or onboarding based on the fact
   * whether the user just created an account or logged in
   */
  const redirectAfterLoginOrSignup: TRedirectAfterLoginOrSignup = useCallback(
    async (authSessionData: AuthSessionData) => {
      await handleRedirectAfterAuth(store, authSessionData);
    },
    [store],
  );

  /**
   * Clears all data stored in indexDB and terminates the firestore connection
   * after the user logged out
   */
  const logout = useCallback(
    async (logOutAllDevices?: boolean) => await handleLogout(store, logOutAllDevices, firebaseApp),
    [store],
  );

  /**
   * Initializes the user and the services and managers such as sentry or today's task local storage manager.
   * It runs only once per app start (this includes hard page reload as well)
   */
  const initialize = useCallback(
    () =>
      handleInitialize(
        store,
        loginOrRegisterInProgress,
        authStateRef,
        handleSetAuthState,
        firebaseApp,
      ),
    [store],
  );

  /**
   * Handles login and register with auth providers on cordova devices
   */
  const cordovaOauth = useCallback(
    (providerId: AuthProviders, firebaseAuth: Auth): Promise<UserCredential> =>
      handleCordovaOauth(providerId, firebaseAuth),
    [],
  );

  /**
   * Calls an authenticate endpoint that depending on the payload creates / reads / modifies user and wedding data
   * from / in firebase whether the user just created an account / logged in into existing account or joins a wedding as
   * a collaborator with new or existing account.
   * On success, the most recent user and wedding data is saved in store and in indexDB to speed up the "initialize" process
   * the next time the app is started.
   * On fail, no data is modified / created in the database, new account is deleted and in the case of the existing one it is logged out.
   * The notice is also displayed to the user that something went wrong and suggestion on what to do next (contact support or try again)
   */
  const setupUserAfterLoginOrSignup = useCallback(
    async (firebaseUser: FirebaseUser, countryCode: CountryCodes, locale: string) => {
      const captchaToken = executeRecaptcha ? await executeRecaptcha('authenticate') : undefined;
      return handleSetupUserAfterLoginOrSignup(
        store,
        firebaseUser,
        handleSetAuthState,
        logout,
        countryCode,
        locale,
        nonceId,
        nonceSecret,
        captchaToken,
      );
    },
    [store, logout, nonceId, nonceSecret, executeRecaptcha],
  );

  /**
   * Logs into an existing firebase-auth account with email and password provider only
   */
  const login: FirebaseContextType['login'] = useCallback(
    async (authRequest) =>
      await handleLogin(store, authRequest, loginOrRegisterInProgress, setupUserAfterLoginOrSignup),
    [store, setupUserAfterLoginOrSignup],
  );

  /**
   * Creates a new firebase-auth account with email and password, social providers or magic link
   * or
   * logs into an existing firebase-auth account with social providers or magic link
   */
  const register: FirebaseContextType['register'] = useCallback(
    async (authRequest) =>
      await handleRegister(
        store,
        authRequest,
        loginOrRegisterInProgress,
        setupUserAfterLoginOrSignup,
        cordovaOauth,
        firebaseApp,
      ),
    [store, cordovaOauth, setupUserAfterLoginOrSignup],
  );

  const loginRegisterWithMagicToken = useCallback(
    async (magicToken: string) => {
      let token = magicToken;

      /**
       * Attempt to exchange a Bridebook magic token for a short-lived Firebase custom token.
       */
      if (JWT.isCustomToken(token) !== true) {
        token = (await fetchy().post('/api/auth/magic', null, { token: magicToken })).token;
      }

      await register({
        type: 'magic',
        token,
        locale: market.locale,
        countryCode: market.country,
      });
    },
    [market.country, market.locale, register],
  );

  useEffect(() => {
    if (typeof magicToken === 'string' && !triggeredMagicLogin.current) {
      triggeredMagicLogin.current = true;
      /**
       * Remove the magic token from the URL to prevent it from being reused and to clean up the URL. It is already saved in ref.
       * We use shallow routing here to avoid triggering a full page reload, which allows us to maintain the current state of the app without interruption.
       */
      router.replace({ query }, undefined, {
        shallow: true,
      });

      loginRegisterWithMagicToken(magicToken);
    }
  }, [loginRegisterWithMagicToken, magicToken, query, router]);

  const loginOrSignupWithGoogle: FirebaseContextType['loginOrSignupWithGoogle'] = useCallback(
    async (params) => await register({ type: 'social', provider: AuthProviders.GOOGLE, ...params }),
    [register],
  );

  const loginOrSignupWithApple: FirebaseContextType['loginOrSignupWithApple'] = useCallback(
    async (params) => await register({ type: 'social', provider: AuthProviders.APPLE, ...params }),
    [register],
  );

  const loginOrSignupWithFacebook: FirebaseContextType['loginOrSignupWithFacebook'] = useCallback(
    async (params) =>
      await register({ type: 'social', provider: AuthProviders.FACEBOOK, ...params }),
    [register],
  );

  /**
   * Marks user as deleted in the firebase and logs the user out
   */
  const deleteAccount = useCallback(
    async () => await handleDeleteAccount(store, logout, t),
    [store, logout, t],
  );

  /**
   * [coupleside and cms] Changes user password in firebase-auth and on success
   * logs in the coupleside users or redirects to the continueUrl if the url was passed (mainly cms login page url for cms users)
   */
  const changePassword: FirebaseContextType['changePassword'] = useCallback(
    async (actionCode, authFields, countryCode, locale, continueUrl) =>
      await handleChangePassword(
        store,
        actionCode,
        authFields,
        login,
        redirectAfterLoginOrSignup,
        countryCode,
        locale,
        continueUrl,
      ),
    [store, login, redirectAfterLoginOrSignup],
  );

  useEffect(() => {
    //make sure the "initialize" method runs only once
    if (!alreadyInitializedRef.current) {
      alreadyInitializedRef.current = true;
      initialize();
    } else {
      SentryMinimal().captureMessage(
        '[Auth context] The auth context was to be initialized more than once. Please check dependencies in auth-context methods.',
      );
    }
  }, [initialize]);

  const triggerRedirectAfterLoginOrSignup = useCallback(async () => {
    const authResult = authResultRef.current;
    assertExists(authResult);

    if (authResult && authResult.status === 'success') {
      await redirectAfterLoginOrSignup(authResult);
    }

    authResultRef.current = null;
  }, [redirectAfterLoginOrSignup]);

  /*
    prepareRedirectAfterLoginOrSignup and triggerRedirectAfterLoginOrSignup are needed when we want to display something, like an animation, after registration.
    For example, we perform registration, store the result by using prepareRedirectAfterLoginOrSignup, display an animation, and after the registration is complete, we initiate the redirection
  */
  const prepareRedirectAfterLoginOrSignup = useCallback((authResult: LoginOrSignupResult) => {
    authResultRef.current = authResult;
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: authState.isInitialized,
      isAuthenticated: authState.isAuthenticated,
      user: authState.user,
      wedding: authState.wedding,
      login,
      loginOrSignupWithGoogle,
      loginOrSignupWithApple,
      loginOrSignupWithFacebook,
      register,
      logout,
      deleteAccount,
      redirectAfterLoginOrSignup,
      changePassword,
      triggerRedirectAfterLoginOrSignup,
      prepareRedirectAfterLoginOrSignup,
    }),
    [
      authState.isAuthenticated,
      authState.isInitialized,
      authState.user,
      authState.wedding,
      login,
      loginOrSignupWithGoogle,
      loginOrSignupWithApple,
      loginOrSignupWithFacebook,
      register,
      logout,
      deleteAccount,
      redirectAfterLoginOrSignup,
      changePassword,
      triggerRedirectAfterLoginOrSignup,
      prepareRedirectAfterLoginOrSignup,
    ],
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
